// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import * as Redux from 'redux';
import control from 'ol/control';
import ScaleLine from 'ol/control/scaleline';
import OlMap from 'ol/map';
import proj from 'ol/proj';
import View from 'ol/view';
import SourceVector from 'ol/source/vector';
import proj4 from 'proj4';
import styled from 'styled-components';
import MeasurePopup from './popups/MeasurePopup';
import BasemapsPopup from './popups/BasemapsPopup';
import LayerListPopup from './popups/LayerListPopup';
import CenterViewButton from './centerViewButton/CenterViewButton';
import PhotoLayer from './photos/PhotoLayer';
import VideoLayer from './videos/VideoLayer';
import FlightLayer from './flights/FlightLayer';
import FaultLayer from './faults/FaultsLayer';
import CommentLayer from './comments/CommentsLayer';
import RotationSlider from './rotationSlider/RotationSlider';
import AttributeInspector from './attributeInspector/AttributeInspector';
import GeoserverLayers from './geoserverLayers/GeoserverLayers';
import shadeColor from './../../../utilities/shadeColor';
import messageManager from './../../../utilities/messageManager';
import { translate } from "react-i18next";
import { getSelectInteractionManager } from './../../../utilities/selectInteractionManager';
import { setOlLayers, centerMap, setBoxExtent } from './../../../actions/map';
import './../../../css/ol/ol.css';
import throttle from './../../../utilities/throttle';
import OlGeomPolygon from 'ol/geom/polygon';

const StyledMapWrapper = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  .ol-control{
    background: none;
    button {
      width: 30px;
      height: 30px;
      cursor: pointer;
      background-color: #265a9c;
      :hover{
        background-color: ${shadeColor('#265a9c', 0.2)};
      }
    }
  }
  .ol-control.ol-zoom button{
    background-color: #464646;
    :hover {
        background-color: ${shadeColor('#727272', 0.2)};
      }
  }
  .ol-control.ol-rotate {
    display: none;
    top: 0.5em;
    left: 2.5em;
    position: absolute;
    width: 32px;
    button{
      background-color: #464646;
      :hover {
        background-color: #727272;
      }
    }
  }
`;

const PopupWrapper = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  pointer-events: none;
  box-shadow: inset 0 0 5px rgba(0,0,0,0.75);
`;

type MapWrapperProps = {
  center: Array<number>,
  extent: Array<number>,
  resizeObservable: object,
  centerMap: (Array<number, number>) => void,
  setBoxExtent: OlGeomPolygon,
}

export class MapWrapper extends React.Component<MapWrapperProps> {
  domNode: any;
  olMap: OlMap;
  zoomControl: object;
  attributesControl: object;

  constructor(props) {
    super(props);
    const { center, resizeObservable }: MapWrapperProps = props;

    proj4.defs("EPSG:2180", "+proj=tmerc +lat_0=0 +lon_0=19 +k=0.9993 +x_0=500000 +y_0=-5300000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");

    // Britihs National Grid
    proj4.defs("EPSG:27700","+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs");

    proj4.defs("EPSG:2176", "+proj=tmerc +lat_0=0 +lon_0=15 +k=0.999923 +x_0=5500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");
    proj4.defs("EPSG:2177", "+proj=tmerc +lat_0=0 +lon_0=18 +k=0.999923 +x_0=6500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");
    proj4.defs("EPSG:2178", "+proj=tmerc +lat_0=0 +lon_0=21 +k=0.999923 +x_0=7500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");
    proj4.defs("EPSG:2179", "+proj=tmerc +lat_0=0 +lon_0=24 +k=0.999923 +x_0=8500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs ");

    proj.setProj4(proj4);
    proj.get('EPSG:2180').axiosOrientation = 'neu';

    const drawSource = new SourceVector();
    drawSource.on('change', function (event) {
      if (drawSource.getFeatures().length > 0) {
        drawSource.clear();
      }
    });

    this.olMap = new OlMap({
      controls: control.defaults().extend([
        new ScaleLine(),
      ]),
      view: new View({
        projection: 'EPSG:3857',
        center,
        zoom: 16,
      }),
    });

    this.zoomControl = this.olMap.getControls().array_[0];
    this.attributesControl = this.olMap.getControls().array_[2];

    resizeObservable.subscribe(() => {
      this.olMap.updateSize();
    });
  }

  componentDidMount(): void {
    this.olMap.setTarget(this.domNode);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const { longitude, latitude } = position.coords;
          const transformedCoords = proj.transform([longitude, latitude], "EPSG:4326", "EPSG:3857");
          this.props.centerMap(transformedCoords);
        },
        error => {
          messageManager.show({
            type: 'error',
            text: this.props.t('Cannot get location'),
          })
        },
        {
          enableHighAccuracy: false,
          timeout: 4000,
          maximumAge: 3600000,
        }
      )
    };
    getSelectInteractionManager('map').createSelectInteraction(this.olMap);
    if (this.props.selected === 'gallery') {
      this.olMap.on('moveend', this.onMoveEnd);
    };

    this.props.setOlLayers(this.olMap.getLayers());
  }

  componentWillUnmount(): void {
    getSelectInteractionManager('map').removeSelectInteraction(this.olMap);
    this.olMap.un('moveend', this.onMoveEnd);
  }

  componentDidUpdate(prevProps): void {
    const { center, extent, t, selected }: MapProps = this.props;
    if (selected === 'gallery' && prevProps.selected !== 'gallery') {
      this.olMap.on('moveend', this.onMoveEnd);
    } 
    else if (selected !== 'gallery' && prevProps.selected === 'gallery') {
      this.olMap.un('moveend', this.onMoveEnd);
    }

    // centrowanie mapy
    const view = this.olMap.getView();
    //const prevCenter: Array<number> = view.options_.center;
    
    if (center && center !== prevProps.center) {
      view.animate({
        center,
        duration: 1000,
        //zoom: 16
      });
    }

    if (extent)
    {
      const prevExtent = prevProps.extent;
      if (!prevExtent || prevExtent[0] !== extent[0] || prevExtent[1] !== extent[1] || prevExtent[2] !== extent[2] || prevExtent[3] !== extent[3]) {
        view.fit(extent, {
          duration: 1000,
        });
      }
    }

    this.zoomControl.element.childNodes[0].title = t('Zoom in');
    this.zoomControl.element.childNodes[1].title = t('Zoom out');
    this.attributesControl.element.childNodes[1].title = t('Attributions');
  }

  onMoveEnd = () => {   
    throttle.run(() => {      
      this.props.setBoxExtent((()=>{   
        //get extent of map box as Polygon
        const mapBox = this.domNode;
        const x = mapBox.clientWidth;
        const y = mapBox.clientHeight;
        const LeftTop = this.olMap.getCoordinateFromPixel([0,0]);   
        const boxExtent = new OlGeomPolygon([[
          LeftTop,
          this.olMap.getCoordinateFromPixel([0,y]),
          this.olMap.getCoordinateFromPixel([x,y]),
          this.olMap.getCoordinateFromPixel([x,0]),
          LeftTop
        ]]);
        return boxExtent;
      })());
    }, 500) 
  }

  render(): React.Node {
    const { chosenProject } = this.props;
    const { license } = chosenProject;

    return (
      <StyledMapWrapper id="map" className="map" innerRef={(node) => { this.domNode = node }}>
        <PopupWrapper>
          <BasemapsPopup olMap={this.olMap} />
          <MeasurePopup olMap={this.olMap} />
          <LayerListPopup olMap={this.olMap} />
        </PopupWrapper>
        <FlightLayer olMap={this.olMap} />
        {
          license.faultsEnabled
            ? <FaultLayer olMap={this.olMap} />
            : null
        }
        <CommentLayer olMap={this.olMap} />
        <VideoLayer olMap={this.olMap} />
        <PhotoLayer olMap={this.olMap} />
        <GeoserverLayers olMap={this.olMap} />
        <CenterViewButton olMap={this.olMap} centerMap={this.props.centerMap} />
        <RotationSlider olMap={this.olMap} />
        <AttributeInspector olMap={this.olMap} />
      </StyledMapWrapper >
    );
  }
};

const mapStateToProps = state => ({
  center: state.mapData.get('center'),
  extent: state.mapData.get('extent'),
  chosenProject: state.auth.get('chosenProject'),
  selected: state.modules.get('selected'),
});

const mapDispatchToProps = dispatch => Redux.bindActionCreators({
  centerMap,
  setBoxExtent,
  setOlLayers,
}, dispatch);

export default translate("translations")(connect(mapStateToProps, mapDispatchToProps)(MapWrapper));



// WEBPACK FOOTER //
// ./src/containers/content/map/MapWrapper.jsx